<template>
	<div>
		<div class="stage">
			<div class="row margin-b20">
				<div class="col-xs-12 col-sm-6 col-md-6 col-lg-6">
					<back-button to="/siptrunks" />
				</div>
				<div class="col-xs-12 col-sm-6 col-md-6 col-lg-6">
					<action-buttons
						:disabled="!editTrunk.username || !editTrunk.tenant || !isPasswordValid"
						:save-label="editTrunk.id ? 'Save' : 'Create'"
						@save="save"
						@close="routeTo('/siptrunks')"
					/>
				</div>
			</div>

			<div class="row margin-b10">
				<div class="col-xs-12 col-sm-12 col-md-12 col-lg-6 col-lg-offset-3">
					<card-wrapper :loading="loading">
						<div class="mx-auto mb-5 w-full">
							<div class="col-sm-12 textCenter">
								<div class="navAvatar margin-b10 flex w-full justify-center">
									<img src="/assets/images/teleflow-logo-black.png" width="200" />
								</div>
							</div>
						</div>

						<div v-if="editTrunk.id" class="flex w-full justify-center">
							<span class="fa-light fa-lock mr-2 mt-2 text-primary"></span>
							<h1>{{ editTrunk.username }}</h1>
						</div>

						<form-section title="General">
							<form-field v-if="!editTrunk.id" label="Username" class="w-full px-2">
								<form-kit
									v-model="editTrunk.username"
									type="text"
									class="form-control"
									placeholder="SIP User"
									name="username"
									:validation="[['required']]"
									:validation-messages="{ matches: 'Must be a valid username' }"
									validation-visibility="live"
								/>
							</form-field>

							<form-field label="Password" class="w-full px-2">
								<div class="flex items-center">
									<input
										v-model="editTrunk.password"
										:type="showPassword ? 'text' : 'password'"
										autocomplete="new-password"
										class="form-control flex-grow"
										:pattern="passwordPattern"
										title="Password must be at least 8 characters long, contain at least one uppercase letter, and one special character."
										@mouseover="showPassword = true"
										@mouseleave="showPassword = false"
									/>
									<button type="button" class="jmiBtn blue ml-2" @click="generatePassword">
										Suggest
									</button>
								</div>
								<span v-if="!isPasswordValid" class="text-xs text-red-500">
									Password must be at least 8 characters long, contain at least one uppercase letter,
									and one special character.
								</span>
							</form-field>

							<form-field
								v-if="
									!editTrunk.id && (tenants?.length > 1 || $global.hasUserRole(userRoles, 'reseller'))
								"
								label="Tenant"
								has-icon
								icon-class="fa-chevron-down"
								class="w-full px-2"
							>
								<select v-model="editTrunk.tenant" @change="editTrunk.account = null">
									<option disabled :value="null">-- Select Tenant --</option>
									<option v-for="t in tenants" :key="t.id" :value="t">
										{{ t.companyName }}
									</option>
								</select>
							</form-field>

							<form-field
								v-if="!editTrunk.id && editTrunk.tenant"
								label="Account"
								has-icon
								icon-class="fa-chevron-down"
								class="w-full px-2"
							>
								<select v-model="editTrunk.account">
									<option :value="null">-- Unassigned - Tenant Trunk --</option>
									<option v-for="a in tenantAccounts" :key="a.id" :value="a">
										{{ a.name }}
									</option>
								</select>
							</form-field>

							<form-field label="Description" class="w-full px-2">
								<input
									v-model="editTrunk.description"
									type="text"
									class="form-control"
									placeholder="Enter description (optional)"
								/>
							</form-field>
						</form-section>

						<form-section title="Caller ID">
							<p
								v-if="editTrunk?.account?.cliPolicy === 'OVERRIDE_FORCED'"
								class="mb-2 text-xs text-red-500"
							>
								Outbound Caller ID is currently overridden by the account settings which will take
								priority over any settings here.
							</p>
							<form-field
								label="Outbound Caller ID (Optional - defaults to username)"
								class="w-full px-2"
							>
								<form-kit
									v-model="editTrunk.cliOverride"
									type="text"
									class="form-control"
									placeholder="Enter Caller ID"
									name="cliOverride"
								/>
							</form-field>
						</form-section>

						<form-section title="Advanced">
							<form-field label="UDP NAT Workaround" class="w-full px-2">
								<input
									v-model="editTrunk.supportNat"
									type="checkbox"
									name="supportNat"
									class="tw-toggle ml-2"
								/>
							</form-field>
						</form-section>

						<div class="row items-center justify-center">
							<action-buttons
								:disabled="!editTrunk.username || !editTrunk.tenant || !isPasswordValid"
								:save-label="editTrunk.id ? 'Save' : 'Create'"
								@save="save"
								@close="routeTo('/siptrunks')"
							/>
						</div>
					</card-wrapper>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import sipTrunksApi from '../scripts/siptrunks/siptrunks.api.js'
import { router } from '../router/index.js'
import accountsApi from '@/scripts/accounts/accounts.api'

export default {
	inject: ['user', 'userRoles', 'tenants'],
	data() {
		return {
			editTrunk: {
				username: '',
				password: '',
				tenant: null,
				account: null,
				description: '',
				overrideCli: ''
			},
			loading: true,
			showPassword: false,
			accounts: [],
			passwordPattern: '^(?=.*[A-Z])(?=.*[!@#$&*]).{8,}$'
		}
	},

	computed: {
		isPasswordValid() {
			const regex = new RegExp(this.passwordPattern)
			return regex.test(this.editTrunk.password)
		},
		tenantAccounts() {
			return this.accounts
				.filter((a) => a.tenant.id === this.editTrunk.tenant?.id)
				.sort((a, b) => a.name.localeCompare(b.name))
		}
	},

	async created() {
		try {
			accountsApi
				.getAll({ limit: 2500 })
				.then((result) => {
					this.accounts = result.data.results
				})
				.catch(console.error)

			if (this.$route.params.id !== 'new') {
				const result = await sipTrunksApi.getById(this.$route.params.id)
				this.editTrunk = result.data
			} else if (this.tenants?.length === 1) {
				this.editTrunk.tenant = this.tenants[0]
			}
			this.loading = false
		} catch (e) {
			this.$swal({
				title: 'Error loading SIP Trunk',
				icon: 'error',
				confirmButtonText: 'Confirm',
				text: 'Unknown error occurred, please try again. If the issue persists, please raise a support ticket.'
			}).then(() => {
				router.push('/siptrunks')
			})
			console.error(e)
		}
	},

	methods: {
		routeTo(routeName) {
			router.push(routeName)
		},
		save() {
			if (!this.editTrunk.id) {
				sipTrunksApi.create(this.editTrunk).then(this.onSave).catch(console.error)
			} else {
				sipTrunksApi.update(this.editTrunk).then(this.onSave).catch(console.error)
			}
		},
		onSave(result) {
			if (result.success === false) {
				if (result.error === 'Duplicate entry') {
					this.$swal({
						title: 'Duplicate Username',
						icon: 'error',
						confirmButtonText: 'Try Again',
						text: 'This username is already in use. Please choose another.'
					})
				} else {
					this.$swal({
						title: 'Error Occurred',
						icon: 'error',
						confirmButtonText: 'Try Again',
						text: 'Unknown error occurred, please try again or contact support.'
					})
				}
			} else this.routeTo('/siptrunks')
		},
		generatePassword() {
			const specialChars = '!@#$&*'
			const upperChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
			const lowerChars = 'abcdefghijklmnopqrstuvwxyz'
			const numbers = '0123456789'

			const allChars = specialChars + upperChars + lowerChars + numbers
			let password = ''

			password += upperChars.charAt(Math.floor(Math.random() * upperChars.length))
			password += specialChars.charAt(Math.floor(Math.random() * specialChars.length))

			for (let i = 0; i < 8; i++) {
				password += allChars.charAt(Math.floor(Math.random() * allChars.length))
			}

			this.editTrunk.password = password
		}
	}
}
</script>
